import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

const LazyTooltipProvider = lazy(() =>
  import("@/components/ui/tooltip").then(m => ({ default: m.TooltipProvider }))
);
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import PublicLayout from "@/components/layout/PublicLayout";
import Index from "./pages/Index";
import DeferredMount from "@/components/DeferredMount";
import { ROUTES, SUPPORTED_LANGS, translatePath, type RouteEntry } from "@/routes/routeMap";

// Lazy-load non-critical UI that isn't needed for first paint
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const CookieConsent = lazy(() => import("./components/CookieConsent"));
const BackToTop = lazy(() => import("./components/BackToTop"));

// Lazy-loaded routes
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const UsersClients = lazy(() => import("./pages/UsersClients"));
const Partners = lazy(() => import("./pages/Partners"));
const Offers = lazy(() => import("./pages/Offers"));
const SolutionsPage = lazy(() => import("./pages/Solutions"));
const SpotWeldingSinks = lazy(() => import("./pages/solutions/SpotWeldingSinks"));
const RoboticSpotWeldingGuns = lazy(() => import("./pages/solutions/RoboticSpotWeldingGuns"));
const CompactingTools = lazy(() => import("./pages/solutions/CompactingTools"));
const LockWelding = lazy(() => import("./pages/solutions/LockWelding"));
const TensionRingWelding = lazy(() => import("./pages/solutions/TensionRingWelding"));
const BarrelHandleWelding = lazy(() => import("./pages/solutions/BarrelHandleWelding"));
const TappingBarrelWelding = lazy(() => import("./pages/solutions/TappingBarrelWelding"));
const ProtectiveMeshWelding = lazy(() => import("./pages/solutions/ProtectiveMeshWelding"));
const OilTankWelding = lazy(() => import("./pages/solutions/OilTankWelding"));
const DoubleSidedSeamOvenWelding = lazy(() => import("./pages/solutions/DoubleSidedSeamOvenWelding"));
const ChimneyElbowWelding = lazy(() => import("./pages/solutions/ChimneyElbowWelding"));
const WeldDestructiveTest = lazy(() => import("./pages/solutions/WeldDestructiveTest"));
const AluminumFrameWelding = lazy(() => import("./pages/solutions/AluminumFrameWelding"));
const GastronomyMedicine = lazy(() => import("./pages/solutions/GastronomyMedicine"));
const IndustrialPedestalWelders = lazy(() => import("./pages/products/IndustrialPedestalWelders"));
const MultiSpotWeldingMachine = lazy(() => import("./pages/products/MultiSpotWeldingMachine"));
const IndustrialProjectionWelders = lazy(() => import("./pages/products/IndustrialProjectionWelders"));
const IndustrialSeamWelders = lazy(() => import("./pages/products/IndustrialSeamWelders"));
const SeamWelderRK94 = lazy(() => import("./pages/products/SeamWelderRK94"));
const WeldingHeads = lazy(() => import("./pages/products/WeldingHeads"));
const WeldingPincers = lazy(() => import("./pages/products/WeldingPincers"));
const BatteryProcessing = lazy(() => import("./pages/products/BatteryProcessing"));
const Workstation = lazy(() => import("./pages/products/Workstation"));
const SpotWelding = lazy(() => import("./pages/products/SpotWelding"));
const CarSpotters = lazy(() => import("./pages/products/CarSpotters"));
const MicroWelding = lazy(() => import("./pages/products/MicroWelding"));
const ServoWeldingHeads = lazy(() => import("./pages/products/ServoWeldingHeads"));
const DoubleWeldingHeads = lazy(() => import("./pages/products/DoubleWeldingHeads"));
const SpotWeldingDetail = lazy(() => import("./pages/products/spot-welding/SpotWeldingDetail"));
const ButtWelding = lazy(() => import("./pages/products/ButtWelding"));
const ProjectionWelding = lazy(() => import("./pages/products/ProjectionWelding"));
const ICBendingTool = lazy(() => import("./pages/products/ICBendingTool"));
const KnowledgeHub = lazy(() => import("./pages/KnowledgeHub"));
const NewsArticlePage = lazy(() => import("./pages/NewsArticle"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const Services = lazy(() => import("./pages/Services"));
const SpareParts = lazy(() => import("./pages/SpareParts"));
const TipDressers = lazy(() => import("./pages/accessories/TipDressers"));
const BoschRexroth = lazy(() => import("./pages/spare-parts/BoschRexroth"));
const HarmsWende = lazy(() => import("./pages/spare-parts/HarmsWende"));
const Transformers = lazy(() => import("./pages/spare-parts/Transformers"));
const WeldingControls = lazy(() => import("./pages/products/WeldingControls"));
const KtSstAc0c4 = lazy(() => import("./pages/products/welding-controls/KtSstAc0c4"));
const HighCurrentSwitch = lazy(() => import("./pages/products/welding-controls/HighCurrentSwitch"));
const Pxc7100 = lazy(() => import("./pages/products/welding-controls/Pxc7100"));
const Prc7300 = lazy(() => import("./pages/products/welding-controls/Prc7300"));
const ISpot = lazy(() => import("./pages/products/welding-controls/ISpot"));
const Primus = lazy(() => import("./pages/products/welding-controls/Primus"));
const Genius = lazy(() => import("./pages/products/welding-controls/Genius"));
const Consumables = lazy(() => import("./pages/Consumables"));

const queryClient = new QueryClient();

const ADMIN_PATHS = ["/admin"];

/**
 * Map routeMap.ts keys → React element. Order MUST mirror ROUTES entries
 * so we can drive both EN and SL routes from the same source of truth.
 */
const ROUTE_ELEMENTS: Record<string, JSX.Element> = {
  home:                  <Index />,
  aboutUs:               <AboutUs />,
  knowledgeHub:          <KnowledgeHub />,
  newsArticle:           <NewsArticlePage />,
  testimonials:          <Testimonials />,
  usersClients:          <UsersClients />,
  partners:              <Partners />,
  faq:                   <FAQPage />,
  services:              <Services />,
  consumables:           <Consumables />,
  privacy:               <PrivacyPolicy />,
  solutions:             <SolutionsPage />,
  solGastronomy:         <GastronomyMedicine />,
  solSinks:              <SpotWeldingSinks />,
  solRoboticSpotGuns:    <RoboticSpotWeldingGuns />,
  solCompacting:         <CompactingTools />,
  solLock:               <LockWelding />,
  solTensionRing:        <TensionRingWelding />,
  solBarrelHandle:       <BarrelHandleWelding />,
  solTappingBarrel:      <TappingBarrelWelding />,
  solProtectiveMesh:     <ProtectiveMeshWelding />,
  solOilTank:            <OilTankWelding />,
  solDoubleSidedSeamOven:<DoubleSidedSeamOvenWelding />,
  solChimneyElbow:       <ChimneyElbowWelding />,
  solWeldDestrTest:      <WeldDestructiveTest />,
  solAluminumFrame:      <AluminumFrameWelding />,
  products:              <Offers />,
  prodPedestal:          <IndustrialPedestalWelders />,
  prodMultiSpot:         <MultiSpotWeldingMachine />,
  prodProjectionInd:     <IndustrialProjectionWelders />,
  prodSeamInd:           <IndustrialSeamWelders />,
  prodSeamRK94:          <SeamWelderRK94 />,
  prodWeldingHeads:      <WeldingHeads />,
  prodWeldingPincers:    <WeldingPincers />,
  prodBattery:           <BatteryProcessing />,
  prodWorkstation:       <Workstation />,
  prodSpotWelding:       <SpotWelding />,
  prodSpotWeldingItem:   <SpotWeldingDetail />,
  prodCarSpotters:       <CarSpotters />,
  prodMicroWelding:      <MicroWelding />,
  prodServoHeads:        <ServoWeldingHeads />,
  prodDoubleHeads:       <DoubleWeldingHeads />,
  prodButtWelding:       <ButtWelding />,
  prodProjectionWelding: <ProjectionWelding />,
  prodICBending:         <ICBendingTool />,
  prodWeldControls:      <WeldingControls />,
  prodWcKtSst:           <KtSstAc0c4 />,
  prodWcHcs:             <HighCurrentSwitch />,
  prodWcPxc7100:         <Pxc7100 />,
  prodWcPrc7300:         <Prc7300 />,
  prodWcISpot:           <ISpot />,
  prodWcPrimus:          <Primus />,
  prodWcGenius:          <Genius />,
  spareParts:            <SpareParts />,
  spareBosch:            <BoschRexroth />,
  spareHarms:            <HarmsWende />,
  spareTransformers:     <Transformers />,
  spareWeldControls:     <WeldingControls />,
  accessories:           <Navigate to="../accessories/tip-dressers" replace />,
  accTipDressers:        <TipDressers />,
};

/** Convert "/products/welding-heads" → "products/welding-heads" for nested route paths */
const stripLead = (p: string) => (p === "/" ? "" : p.replace(/^\//, ""));

/**
 * Detects preferred language for a fresh visitor.
 * Priority: localStorage saved > browser language (sl* → sl) > en default.
 */
function detectLang(): "en" | "sl" {
  if (typeof window === "undefined") return "en";
  try {
    const saved = window.localStorage.getItem("preferred-lang");
    if (saved === "sl" || saved === "en") return saved;
  } catch {
    /* ignore */
  }
  const navLang = (typeof navigator !== "undefined" && navigator.language) || "";
  return navLang.toLowerCase().startsWith("sl") ? "sl" : "en";
}

/**
 * Catches any URL without a /:lang prefix and redirects to the lang-prefixed
 * (and slug-translated) version, preserving the search string and hash.
 * Admin routes are excluded.
 */
const LegacyRedirect = () => {
  const { pathname, search, hash } = useLocation();

  if (ADMIN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return null;
  }

  // Guard: if pathname already starts with a valid lang, don't re-prefix
  // (prevents redirect loops).
  const firstSeg = pathname.split("/")[1];
  if ((SUPPORTED_LANGS as readonly string[]).includes(firstSeg)) {
    return null;
  }

  const lang = detectLang();
  const stripped = pathname === "/" ? "" : pathname.replace(/\/$/, "");
  const translated = stripped === "" ? "" : translatePath(stripped, lang);
  const target = `/${lang}${translated}${search}${hash}`;
  return <Navigate to={target} replace />;
};

/**
 * Validates the :lang path param. If invalid, redirect to detected language
 * preserving the rest of the URL. Also collapses stacked language segments
 * (e.g. `/en/en/foo` → `/en/foo`) to prevent infinite-length URLs and
 * downstream history.replaceState() loops.
 */
const LangGate = () => {
  const { pathname, search, hash } = useLocation();
  const segs = pathname.split("/").filter(Boolean);

  // Collapse any stacked language prefixes (/en/en/.., /en/sl/en/.., etc.)
  // Use the FIRST segment as the effective language; strip all consecutive
  // leading lang segments before checking for the real content.
  const firstLang = (SUPPORTED_LANGS as readonly string[]).includes(segs[0])
    ? (segs[0] as "en" | "sl")
    : null;

  if (!firstLang) {
    const detected = detectLang();
    return <Navigate to={`/${detected}${search}${hash}`} replace />;
  }

  let i = 0;
  while (i < segs.length && (SUPPORTED_LANGS as readonly string[]).includes(segs[i])) {
    i++;
  }
  if (i > 1) {
    const rest = segs.slice(i).join("/");
    const clean = `/${firstLang}${rest ? "/" + rest : ""}${search}${hash}`;
    return <Navigate to={clean} replace />;
  }

  return <PublicLayout />;
};

/**
 * Build the public-route children. For each entry in ROUTES we emit ONE route
 * with the slug for the active lang. Two parents (`/en` and `/sl`) wrap them.
 */
function buildChildRoutes(lang: "en" | "sl") {
  return ROUTES.map((r: RouteEntry) => {
    const element = ROUTE_ELEMENTS[r.key];
    if (!element) return null;
    const slug = r[lang];
    if (slug === "/") {
      return <Route key={r.key} index element={element} />;
    }
    return <Route key={r.key} path={stripLead(slug)} element={element} />;
  });
}

/**
 * For each route where the SL slug differs from the EN slug, emit a redirect
 * under /sl from the legacy EN slug to the new SL slug. Keeps old indexed
 * `/sl/<en-slug>` URLs working (e.g. /sl/accessories/tip-dressers →
 * /sl/dodatna-oprema/rocni-posnemalec-elektrod).
 */
function buildSlLegacyRedirects() {
  return ROUTES.filter((r) => r.en !== r.sl && r.en !== "/").map((r) => (
    <Route
      key={`sl-legacy-${r.key}`}
      path={stripLead(r.en)}
      element={<Navigate to={`/sl${r.sl}`} replace />}
    />
  ));
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <LanguageProvider>
        <Suspense fallback={null}><LazyTooltipProvider>
          <DeferredMount>
            <Suspense fallback={null}>
              <Toaster />
              <Sonner />
            </Suspense>
          </DeferredMount>
          <DeferredMount>
            <Suspense fallback={null}>
              <CookieConsent />
              <BackToTop />
            </Suspense>
          </DeferredMount>
          <Suspense fallback={null}>
            <Routes>
              {/* English routes */}
              <Route path="/en" element={<LangGate />}>
                {buildChildRoutes("en")}
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Slovenian routes (translated slugs) */}
              <Route path="/sl" element={<LangGate />}>
                {buildChildRoutes("sl")}
                {buildSlLegacyRedirects()}
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Admin routes (no language prefix) */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/baza" element={<Suspense fallback={null}><ProtectedRoute><AdminDashboard /></ProtectedRoute></Suspense>} />

              {/* Legacy fallback — redirects any non-prefixed URL (incl. "/") to /:lang/...  */}
              <Route path="*" element={<LegacyRedirect />} />
            </Routes>
          </Suspense>
        </LazyTooltipProvider></Suspense>
      </LanguageProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
